/**
 * @file mindmapGraph.ts
 * @brief Single source for meeting-mindmap shape, visuals and graph layout.
 *
 * @details
 * The mindmap arrives as a **flat** `nodes[]` with `parentId` links (see
 * `MeetingMindmapSchema` in ../schemas.ts). Two surfaces render it and they
 * must never disagree about what a node *is*:
 *   - the collapsible list  -> MeetingDetailView.tsx (`MindmapTab`)
 *   - the React Flow canvas -> MeetingMindmapCanvas.tsx
 * So the tree builder, the per-type icon/colour, and the default-expanded rule
 * live here once (04-08-2026). They previously lived inline in
 * MeetingDetailView.tsx; moving them out is why that file no longer defines
 * `buildMindmapTree` / `mindmapVisual`.
 *
 * `layoutMindmap` is the extra piece the canvas needs: a left-to-right tidy
 * tree. We lay out by hand rather than pulling in dagre/elk — the graph is a
 * shallow rooted tree (root -> topic -> decision/action/risk), which is the one
 * case where the classic leaf-stacking pass is a few dozen lines and exact.
 * Node heights are *estimated* from label length because React Flow measures
 * only after the first paint, and a layout that shifts once measurement lands
 * looks broken; the estimate is deliberately generous and clamped to 3 lines,
 * which is also where the node's CSS clamps the text.
 *
 * @dependencies
 * - lucide-react: node-type glyphs (the only icon library — one stroke weight)
 *
 * @references
 * - WEBPANEL/src/features/company-meetings/schemas.ts (MeetingMindmapSchema)
 * - FRONTEND/src/screens/MeetingIntelligence/MeetingDetails/mindmapUtils.ts
 *   (mobile twin — `getMindmapNodeVisual` / `buildMindmapTree`; mobile Map
 *   draws naked icons + tree elbows since 29-09-2026, no circle chrome)
 *
 * @author DES
 * @date 04-08-2026
 */

import {
  Check,
  Circle,
  Layers,
  ListChecks,
  Network,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";
import type { MeetingMindmap } from "../schemas";

// ----------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------
export type Mindmap = MeetingMindmap;
export type MindmapNodeData = Mindmap["nodes"][number];
export type MindmapTreeNode = MindmapNodeData & { children: MindmapTreeNode[] };

// ----------------------------------------------------------------------
// Per-type visuals
// Mirrors mobile `getMindmapNodeVisual` (mindmapUtils.ts) name-for-name.
// Colours are literals, not theme tokens, on purpose: a decision must be the
// same green on both clients and in both webpanel themes.
// ----------------------------------------------------------------------
export function mindmapVisual(type?: string | null): {
  Icon: LucideIcon;
  color: string;
} {
  switch (type) {
    case "root":
      return { Icon: Network, color: "#2563EB" };
    case "topic":
      return { Icon: Layers, color: "#6366F1" };
    // Glyphs corrected to the phone's (30-09-2026): Check, ListChecks and
    // TriangleAlert. The web had CircleCheck / SquareCheck / CircleAlert.
    case "decision":
      return { Icon: Check, color: "#10B981" };
    case "action":
      return { Icon: ListChecks, color: "#F59E0B" };
    case "risk":
      return { Icon: TriangleAlert, color: "#EF4444" };
    default:
      return { Icon: Circle, color: "#64748B" };
  }
}

// ----------------------------------------------------------------------
// Flat nodes[] -> nested children arrays (mirrors mobile `buildMindmapTree`)
// ----------------------------------------------------------------------
export function buildMindmapTree(mindmap: Mindmap): MindmapTreeNode[] {
  const byId = new Map<string, MindmapTreeNode>();
  for (const n of mindmap.nodes) byId.set(n.id, { ...n, children: [] });

  const roots: MindmapTreeNode[] = [];
  byId.forEach((node) => {
    if (node.parentId && byId.has(node.parentId)) {
      byId.get(node.parentId)!.children.push(node);
    } else if (node.id === mindmap.rootId || node.type === "root") {
      roots.push(node);
    }
  });

  if (roots.length === 0 && mindmap.rootId && byId.has(mindmap.rootId)) {
    roots.push(byId.get(mindmap.rootId)!);
  }
  if (roots.length === 0) {
    return Array.from(byId.values()).filter((n) => !n.parentId);
  }
  return roots;
}

/** Root + every topic expanded by default (mirrors mobile). */
export function defaultExpandedIds(mindmap: Mindmap): Set<string> {
  const ids = new Set<string>();
  if (mindmap.rootId) ids.add(mindmap.rootId);
  mindmap.nodes.filter((n) => n.type === "topic").forEach((n) => ids.add(n.id));
  return ids;
}

/** The phone's default: only the root open (mobile getDefaultExpandedNodeIds). */
export function rootExpandedIds(mindmap: Mindmap): Set<string> {
  const ids = new Set<string>();
  if (mindmap.rootId) ids.add(mindmap.rootId);
  return ids;
}

/**
 * Lift nested `action` nodes up beside their decision, so actions share the
 * decisions' indent under a topic. Port of mobile `hoistMindmapActions`
 * (mindmapUtils.ts, DES 29-09-2026).
 */
export function hoistMindmapActions(nodes: MindmapTreeNode[]): MindmapTreeNode[] {
  return nodes.map(hoistActionsInNode);
}

function withoutActions(node: MindmapTreeNode): MindmapTreeNode {
  return {
    ...node,
    children: node.children.filter((c) => c.type !== "action").map(hoistActionsInNode),
  };
}

function collectNestedActions(node: MindmapTreeNode): MindmapTreeNode[] {
  const out: MindmapTreeNode[] = [];
  for (const child of node.children) {
    if (child.type === "action") out.push(withoutActions(child));
    out.push(...collectNestedActions(child));
  }
  return out;
}

function hoistActionsInNode(node: MindmapTreeNode): MindmapTreeNode {
  if (node.type !== "root" && node.type !== "topic") {
    return { ...node, children: node.children.map(hoistActionsInNode) };
  }
  const next: MindmapTreeNode[] = [];
  for (const child of node.children) {
    if (child.type === "topic" || child.type === "root") {
      next.push(hoistActionsInNode(child));
    } else {
      next.push(withoutActions(child));
      next.push(...collectNestedActions(child));
    }
  }
  return { ...node, children: next };
}

/** Every node that actually has children — the "expand all" target. */
export function branchIds(tree: MindmapTreeNode[]): Set<string> {
  const ids = new Set<string>();
  const walk = (n: MindmapTreeNode) => {
    if (n.children.length > 0) ids.add(n.id);
    n.children.forEach(walk);
  };
  tree.forEach(walk);
  return ids;
}

// ----------------------------------------------------------------------
// Left-to-right tidy-tree layout (canvas only)
// Column x is depth-driven; y comes from stacking leaves and centring each
// parent on its children, then a per-column pass that pushes apart anything
// still overlapping (a parent taller than its single child's slot).
// Consumed by: MeetingMindmapCanvas.tsx
// ----------------------------------------------------------------------
export const MINDMAP_NODE_WIDTH = 236;
const COLUMN_GAP = 76;
const ROW_GAP = 18;
const CHARS_PER_LINE = 26;
const MAX_LABEL_LINES = 3;

/** Estimated rendered height — must match the node's padding + clamped text. */
export function mindmapNodeHeight(node: MindmapNodeData): number {
  const label = node.label ?? "";
  const lines = Math.min(
    MAX_LABEL_LINES,
    Math.max(1, Math.ceil(label.length / CHARS_PER_LINE)),
  );
  // 20px vertical padding x2 + 18px per label line + 14px type caption
  return 40 + lines * 18 + (node.type && node.type !== "root" ? 14 : 0);
}

export type MindmapLayoutNode = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  depth: number;
  node: MindmapNodeData;
  childCount: number;
  collapsed: boolean;
};

export type MindmapLayoutEdge = {
  id: string;
  source: string;
  target: string;
  /** Accent of the *target* — an edge is coloured by what it leads to. */
  color: string;
};

export function layoutMindmap(
  tree: MindmapTreeNode[],
  expanded: Set<string>,
): { nodes: MindmapLayoutNode[]; edges: MindmapLayoutEdge[] } {
  const nodes: MindmapLayoutNode[] = [];
  const edges: MindmapLayoutEdge[] = [];
  let leafCursor = 0;

  // Returns the vertical centre of the placed subtree so the parent can sit
  // on the midpoint of its first and last child.
  const place = (node: MindmapTreeNode, depth: number): number => {
    const height = mindmapNodeHeight(node);
    const isOpen = expanded.has(node.id);
    const children = isOpen ? node.children : [];

    let center: number;
    if (children.length === 0) {
      center = leafCursor + height / 2;
      leafCursor += height + ROW_GAP;
    } else {
      const childCenters = children.map((child) => place(child, depth + 1));
      center =
        (childCenters[0] + childCenters[childCenters.length - 1]) / 2;
    }

    nodes.push({
      id: node.id,
      x: depth * (MINDMAP_NODE_WIDTH + COLUMN_GAP),
      y: center - height / 2,
      width: MINDMAP_NODE_WIDTH,
      height,
      depth,
      node,
      childCount: node.children.length,
      collapsed: node.children.length > 0 && !isOpen,
    });

    for (const child of children) {
      edges.push({
        id: `${node.id}->${child.id}`,
        source: node.id,
        target: child.id,
        color: mindmapVisual(child.type).color,
      });
    }

    return center;
  };

  tree.forEach((root) => {
    place(root, 0);
    leafCursor += ROW_GAP * 2; // breathing room between multiple roots
  });

  resolveColumnOverlaps(nodes);
  return { nodes, edges };
}

/**
 * Leaf-stacking centres parents perfectly but says nothing about parents
 * colliding with each other — a two-line topic centred on one short child can
 * overlap the topic above it. Push the lower one down; the edges are curves,
 * so a few px of asymmetry costs nothing and an overlap costs readability.
 */
function resolveColumnOverlaps(nodes: MindmapLayoutNode[]): void {
  const byDepth = new Map<number, MindmapLayoutNode[]>();
  for (const n of nodes) {
    const bucket = byDepth.get(n.depth);
    if (bucket) bucket.push(n);
    else byDepth.set(n.depth, [n]);
  }

  byDepth.forEach((column) => {
    column.sort((a, b) => a.y - b.y);
    for (let i = 1; i < column.length; i += 1) {
      const prev = column[i - 1];
      const minY = prev.y + prev.height + ROW_GAP;
      if (column[i].y < minY) column[i].y = minY;
    }
  });
}
